import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useTransition,
  type FC,
  type ReactNode,
} from 'react';
import { useSearchInputContext } from 'src/common/components/Search/SearchInputProvider';
import useAfterRenderCallback from 'src/common/hooks/useAfterRenderCallback';

export type SearchHeroModal = 'location' | 'dates' | 'guests';

export type SearchHeroModalContext = {
  active?: SearchHeroModal;
  open: (modal: SearchHeroModal) => void;
  close: () => void;
  openLocation: () => void;
  openDates: () => void;
  openGuests: () => void;
  isLocationOpen: boolean;
  isDatesOpen: boolean;
  isGuestsOpen: boolean;
  opening?: SearchHeroModal;
  /**
   * The same inputs are used in various views, which sometimes
   * needs to change the way they work when activating/gaining focus:
   * - open-sheet: open the mobile sheet from in-page
   * - in-sheet: be used while within the sheet
   * - responsive: automatically resolve to `open-sheet` only when in
   *   a mobile viewport (`SearchInputProvider.isDesktop = false`)
   */
  openType?: 'responsive' | 'in-sheet' | 'open-sheet';
};
const searchHeroModalContext = createContext<SearchHeroModalContext>({
  open: () => {},
  close: () => {},
  openLocation: () => {},
  openDates: () => {},
  openGuests: () => {},
  isLocationOpen: false,
  isDatesOpen: false,
  isGuestsOpen: false,
});

type SearchHeroModalProviderProps = {
  children: ReactNode;
  openType?: SearchHeroModalContext['openType'];
  /** The mobile view is global, so it is shared across all search boxes */
  active?: SearchHeroModalContext['active'];
};
const SearchHeroModalProvider: FC<SearchHeroModalProviderProps> = ({
  children,
  active: initialActive,
  openType = 'responsive',
}) => {
  const [active, setActive] = useState<SearchHeroModal | undefined>(initialActive);
  const { sheet, isDesktop } = useSearchInputContext();
  const [, startTransition] = useTransition();

  // Used to show active selection when the field is used to open the mobile modal (for INP)
  const [opening, setOpening] = useState<SearchHeroModal | undefined>();

  useEffect(() => {
    setActive(initialActive);
  }, [initialActive]);

  const autoOpenType = openType === 'responsive' && !isDesktop
    ? 'open-sheet'
    : openType;

  const openAfterRender = useAfterRenderCallback((name: SearchHeroModal) => {
    if (autoOpenType === 'open-sheet') {
      sheet.open(name);
      // This modal is not actually activated, instead the sheet modal provider
      // is used. This one should be disabled
      setActive(undefined);
      // Only show some interaction has occurred briefly. It is hidden by
      // the mobile modal. It should be disabled when the modal is closed
    } else {
      setActive(name);
    }
    startTransition(() => setOpening(undefined));
  });

  const open = useCallback((name: SearchHeroModal) => {
    if (name === active || !name) return;

    // Immediate reaction to interaction
    setOpening(name);
    // Immediately close unfocused modals. TODO: monitor cost. Could be removed
    setActive(undefined);

    // This current input retains focus, but clicking close immediately after on the mobile
    // modal moves focus back to this, triggering a re-open due to the onFocus listener
    // Ensure all are unfocused
    if (autoOpenType === 'open-sheet') {
      (document.activeElement as HTMLInputElement)?.blur?.();
    }
    openAfterRender(name);
  }, [active, autoOpenType, openAfterRender]);

  const close = useCallback(() => {
    setActive(undefined);
    setOpening(undefined);
  }, []);

  const openLocation = useCallback(() => open('location'), [open]);
  const openDates = useCallback(() => open('dates'), [open]);
  const openGuests = useCallback(() => open('guests'), [open]);

  const value = useMemo(() => ({
    active,
    open,
    close,
    openLocation,
    openDates,
    openGuests,
    isLocationOpen: active === 'location',
    isDatesOpen: active === 'dates',
    isGuestsOpen: active === 'guests',
    openType: autoOpenType,
    opening,
  }), [
    close,
    active,
    open,
    openLocation,
    openDates,
    openGuests,
    autoOpenType,
    opening,
  ]);

  return (
    <searchHeroModalContext.Provider value={value}>
      {children}
    </searchHeroModalContext.Provider>
  );
};

export default SearchHeroModalProvider;

export const useSearchHeroModalContext = (): SearchHeroModalContext => {
  const context = useContext(searchHeroModalContext);
  if (!context) {
    throw new Error('useSearchHeroModalContext must be used within a SearchHeroModalProvider');
  }
  return context;
};
